import { useCallback, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { publicFetch } from '../../utils/authenticatedFetch';
import CONFIG from '../../config/config';

const PAGE_SIZE = 50;

// Offset-based pagination shared by both infinite queries below.
const nextOffsetParam = (lastPage, allPages) => {
  const loaded = allPages.reduce((sum, page) => sum + page.stocks.length, 0);
  return loaded < lastPage.total ? loaded : undefined;
};

export const useEarningsData = (days = 7, watchlistSymbols = null, selectedIndustries = null, lookback = 0) => {
  // When true, the next queryFn call appends nocache=1 so the worker bypasses its 10-min
  // `caches.default` layer and re-reads D1. Set by refetchFresh() before refetch, cleared after.
  const forceNoCacheRef = useRef(false);

  const query = useInfiniteQuery({
    queryKey: [
      'earningsCalendar',
      days,
      watchlistSymbols?.join(',') || 'all',
      selectedIndustries?.length ? selectedIndustries.sort().join(',') : 'all',
      lookback,
    ],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        days: days.toString(),
        offset: pageParam.toString(),
        limit: PAGE_SIZE.toString(),
      });
      if (lookback > 0) {
        params.append('lookback', lookback.toString());
      }
      if (watchlistSymbols?.length) {
        params.append('watchlist', watchlistSymbols.join(','));
      }
      if (selectedIndustries?.length) {
        params.append('industry', selectedIndustries.join(','));
      }
      if (forceNoCacheRef.current) {
        params.append('nocache', '1');
      }
      const url = `${CONFIG.API_URLS.EARNINGS_CALENDAR}?${params}`;
      const response = await publicFetch(url);
      return response.json();
    },
    getNextPageParam: nextOffsetParam,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const refetchFresh = useCallback(async () => {
    forceNoCacheRef.current = true;
    try {
      await query.refetch();
    } finally {
      forceNoCacheRef.current = false;
    }
  }, [query]);

  return { ...query, refetchFresh };
};

// Single-day reporters for the histogram bar-click panel. Paginated ("Show more")
// because busy dates can have 500+ reporters and enrichment is per-stock.
export const useEarningsDay = (date) => {
  return useInfiniteQuery({
    queryKey: ['earningsDay', date],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        date,
        offset: pageParam.toString(),
        limit: PAGE_SIZE.toString(),
      });
      const response = await publicFetch(`${CONFIG.API_URLS.EARNINGS_CALENDAR}?${params}`);
      return response.json();
    },
    getNextPageParam: nextOffsetParam,
    enabled: !!date,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};
